import {
  Alert,
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Button,
  ButtonGroup,
  ModalBodyProps,
  ModalContentProps,
  ModalProps,
  Text,
  VStack,
} from "@chakra-ui/react";
import { ReactNode, useRef } from "react";

export type ConfirmationDialogProps = Omit<ModalProps, "children"> & {
  title?: ReactNode;
  description?: ReactNode;
  warning?: ReactNode;
  isLoading?: boolean;
  loadingText?: string;
  confirmLabel?: string;
  confirmColor?: string;
  cancelLabel?: string;
  closeLabel?: string;
  showCloseAsButton?: boolean;
  onConfirm: () => void;
  onCancel?: () => void;
  _contentProps?: ModalContentProps;
  _bodyProps?: ModalBodyProps;
};

export function ConfirmationDialog({
  isOpen,
  title = "Are you sure?",
  description,
  warning,
  isLoading,
  loadingText,
  confirmLabel = "Confirm",
  confirmColor = "gray",
  cancelLabel = "Cancel",
  closeLabel = "Close",
  showCloseAsButton,
  onConfirm,
  onCancel,
  onClose,
  _contentProps = {},
  _bodyProps = {},
  ...rest
}: ConfirmationDialogProps) {
  const cancelRef = useRef(null);
  const closeRef = useRef(null);
  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={showCloseAsButton ? closeRef : cancelRef}
      onClose={onClose}
      {...rest}
    >
      <AlertDialogOverlay>
        <AlertDialogContent {..._contentProps}>
          <AlertDialogHeader>{title}</AlertDialogHeader>
          {!showCloseAsButton && <AlertDialogCloseButton />}
          <AlertDialogBody align="flex-start" as={VStack} {..._bodyProps}>
            {typeof description === "string" ? (
              <Text as="p">{description}</Text>
            ) : (
              description
            )}

            {typeof warning === "string" ? (
              <Alert alignItems="flex-start" status="warning">
                <AlertIcon />
                <VStack alignItems="flex-start" spacing={4}>
                  <Text as="p">{warning}</Text>
                </VStack>
              </Alert>
            ) : (
              warning
            )}
          </AlertDialogBody>
          <AlertDialogFooter>
            <ButtonGroup spacing={2}>
              {showCloseAsButton && (
                <Button
                  ref={closeRef}
                  disabled={isLoading}
                  variant="ghostDimmed"
                  onClick={onClose}
                >
                  {closeLabel}
                </Button>
              )}
              <Button
                ref={cancelRef}
                disabled={isLoading}
                variant="ghostDimmed"
                onClick={onCancel}
              >
                {cancelLabel}
              </Button>
              <Button
                colorScheme={confirmColor}
                isLoading={isLoading}
                loadingText={loadingText}
                type="submit"
                onClick={onConfirm}
              >
                {confirmLabel}
              </Button>
            </ButtonGroup>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}
